import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import type { FC, ReactNode } from 'react';

type ConfirmTone = 'danger' | 'warning' | 'primary';

interface AccountConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  eyebrow: string;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  tone?: ConfirmTone;
  icon: ReactNode;
}

const toneStyles: Record<
  ConfirmTone,
  { header: string; icon: string; button: 'danger' | 'warning' | 'primary' }
> = {
  danger: {
    header:
      'border-b border-danger-100 bg-gradient-to-br from-danger-50 via-white to-danger-50/40 dark:border-danger-900/40 dark:from-danger-950/40 dark:via-zinc-900 dark:to-danger-950/20',
    icon: 'bg-danger-100 text-danger-700 ring-danger-200 dark:bg-danger-900/50 dark:text-danger-300 dark:ring-danger-800',
    button: 'danger',
  },
  warning: {
    header:
      'border-b border-warning-100 bg-gradient-to-br from-warning-50 via-white to-warning-50/40 dark:border-warning-900/40 dark:from-warning-950/40 dark:via-zinc-900 dark:to-warning-950/20',
    icon: 'bg-warning-100 text-warning-700 ring-warning-200 dark:bg-warning-900/50 dark:text-warning-300 dark:ring-warning-800',
    button: 'warning',
  },
  primary: {
    header:
      'border-b border-primary-100 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:border-zinc-700 dark:from-primary-950/60 dark:via-zinc-900 dark:to-secondary-950/30',
    icon: 'bg-primary-100 text-primary-700 ring-primary-200 dark:bg-primary-900/50 dark:text-primary-300 dark:ring-primary-800',
    button: 'primary',
  },
};

const AccountConfirmModal: FC<AccountConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  eyebrow,
  title,
  description,
  confirmLabel,
  tone = 'primary',
  icon,
}) => {
  const styles = toneStyles[tone];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement='center'
      backdrop='blur'
      size='md'
      classNames={{
        base: 'overflow-hidden border border-zinc-200 shadow-2xl dark:border-zinc-700',
        closeButton:
          'top-3 right-3 text-zinc-500 hover:bg-white/70 hover:text-zinc-950 dark:hover:bg-zinc-800 dark:hover:text-white',
      }}
    >
      <ModalContent>
        <ModalHeader
          className={`relative flex items-start gap-3 overflow-hidden px-6 py-5 ${styles.header}`}
        >
          <span
            className={`flex size-10 shrink-0 items-center justify-center rounded-xl ring-1 ${styles.icon}`}
          >
            {icon}
          </span>
          <span>
            <span className='block text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400'>
              {eyebrow}
            </span>
            <span className='mt-1 block font-serif text-lg font-semibold text-zinc-950 dark:text-white'>
              {title}
            </span>
          </span>
        </ModalHeader>
        <ModalBody className='px-6 py-5'>
          <div className='text-sm leading-6 text-zinc-600 dark:text-zinc-300'>{description}</div>
        </ModalBody>
        <ModalFooter className='border-t border-zinc-100 px-6 py-4 dark:border-zinc-800'>
          <Button variant='flat' onPress={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button color={styles.button} onPress={onConfirm} isLoading={isLoading}>
            {confirmLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AccountConfirmModal;
